import type { UserSummary } from '../types';

/**
 * Format a user role into a display label with specialty or company name
 *
 * @param participant - User summary with role and optional specialty/company
 * @returns Formatted role label for display
 *
 * @example
 * formatRoleLabel({
 *   id: 'user-123',
 *   name: 'Jane Doe',
 *   profilePhoto: '/images/jane.jpg',
 *   role: 'SERVICE_PROVIDER',
 *   specialty: 'Physical Therapist',
 * })
 * // Returns: 'Physical Therapist'
 *
 * @example
 * formatRoleLabel({
 *   id: 'advisor-456',
 *   name: 'John Smith',
 *   profilePhoto: '/images/john.jpg',
 *   role: 'FINANCIAL_ADVISOR',
 *   companyName: 'Morgan Stanley',
 * })
 * // Returns: 'Advisor - Morgan Stanley'
 */
export function formatRoleLabel(participant: UserSummary): string {
  switch (participant.role) {
    case 'SERVICE_PROVIDER':
      return participant.specialty || 'Provider';
    case 'FINANCIAL_ADVISOR':
      return participant.companyName
        ? `Advisor - ${participant.companyName}`
        : 'Advisor';
    case 'CARE_SEEKER':
      return 'Care Seeker';
    default: {
      // Handle unexpected role values defensively
      const roleValue = participant.role as string;
      if (!roleValue) {
        return 'Unknown role';
      }
      const formattedRole = roleValue
        .toLowerCase()
        .split('_')
        .map((word: string) => (word[0] ? word[0].toUpperCase() + word.slice(1) : ''))
        .join(' ')
        .trim();
      return formattedRole || 'Unknown role';
    }
  }
}
